/**
 * 装配体配合控制器状态策略。
 *
 * 文件职责：
 * 1. 提供 PropertyManager 默认状态；
 * 2. 根据当前选择和配合类型解析可用性；
 * 3. 保持控制器主体只负责流程编排。
 */

import {
  AssemblyMateType,
  MateAlignment,
  MateConcentricMisalignmentType,
  MateDistanceMode,
  MateSolveStatus,
  createAssemblyMateId,
  type AssemblyMateDefinition,
} from '../../definition';
import { DEFAULT_CONCENTRIC_MISALIGNMENT_MAXIMUM_DEVIATION } from './AssemblyMateConcentricMisalignmentStatePolicy';
import { isAssemblyMateTypeEnabled } from '../../compatibility';
import { resolveMateCandidatePair, type AssemblyMateSelectionCandidate } from '../../selection';
import { resolveStandardMateDistanceAlignmentEnabled } from '../../solver';
import type { AssemblyMateSolveDiagnostic } from '../../solver';
import {
  canLockConcentricMateRotationEntities,
  isConcentricLockRotationBlockedByOverDefinedReference,
} from '../../definition/AssemblyMateLockRotation';
import type { AssemblyMateControllerState } from '../AssemblyMateControllerTypes';

/**
 * 创建默认控制器状态。
 *
 * @returns 默认状态。
 */
export function createDefaultAssemblyMateControllerState(): AssemblyMateControllerState {
  return {
    active: false,
    ...createEmptyAssemblyMateControllerSessionStatePatch(),
  } as AssemblyMateControllerState;
}

/**
 * 创建空 Mate PropertyManager 会话状态补丁。
 *
 * 设计说明：
 * 1. 打开、取消和新建 Mate 提交后都会回到“等待下一组 Entities to Mate”的空会话；
 * 2. 这些入口必须共享同一套默认值，避免反向对齐、反转尺寸或显示预览状态在某个出口残留；
 * 3. 该函数只描述控制器状态，不触碰文档、预览对象或 Viewer 选择模式。
 *
 * @returns 空 Mate 会话状态补丁。
 */
export function createEmptyAssemblyMateControllerSessionStatePatch(): Omit<AssemblyMateControllerState, 'active'> {
  return {
    draftMateId: createAssemblyMateId(),
    editingMateId: null,
    candidates: [],
    candidateReplacementIndex: null,
    selectionTarget: 'mateEntities',
    angleReferenceEntity: null,
    compatibility: null,
    selectedMateType: null,
    alignment: MateAlignment.Aligned,
    alignmentEnabled: true,
    alignmentBranchAvailability: {
      aligned: true,
      antiAligned: true,
    },
    distance: 1,
    distanceMode: MateDistanceMode.Minimum,
    distanceEntityModes: {
      firstEntityMode: MateDistanceMode.Minimum,
      secondEntityMode: MateDistanceMode.Minimum,
    },
    distanceModeOptions: [],
    firstDistanceModeOptions: [],
    secondDistanceModeOptions: [],
    angle: Math.PI / 6,
    dimensionFlipped: false,
    lockRotation: false,
    lockRotationEnabled: false,
    concentricMisalignmentEnabled: false,
    concentricMisalignmentType: MateConcentricMisalignmentType.AlignLinkedMate,
    concentricMisalignmentLinkedMateId: null,
    concentricMisalignmentMaximumDeviation: DEFAULT_CONCENTRIC_MISALIGNMENT_MAXIMUM_DEVIATION,
    concentricMisalignmentLinkedMateOptions: [],
    showPreview: true,
    firstSelectionTransparent: false,
    solveStatus: MateSolveStatus.NotRun,
    errorMessage: null,
    canSubmit: false,
  };
}

/**
 * 选择当前可用的默认配合类型。
 *
 * @param state 当前状态。
 * @returns 配合类型。
 */
export function resolveDefaultAssemblyMateType(
  state: AssemblyMateControllerState,
): AssemblyMateType | null {
  if (!state.compatibility) {
    return null;
  }
  if (state.selectedMateType && isAssemblyMateTypeEnabled(state.compatibility, state.selectedMateType)) {
    return state.selectedMateType;
  }
  return state.compatibility.mateTypes.find((item) => item.enabled)?.mateType ?? null;
}

/**
 * 解析当前配合类型是否允许切换同向/反向对齐。
 *
 * @param mateType 当前配合类型。
 * @param candidates 当前选择候选。
 * @returns 是否允许切换对齐方向。
 */
export function resolveAssemblyMateAlignmentEnabled(
  mateType: AssemblyMateType | null,
  candidates: readonly AssemblyMateSelectionCandidate[],
): boolean {
  if (!mateType) {
    return false;
  }

  if (mateType === AssemblyMateType.Perpendicular || mateType === AssemblyMateType.Lock) {
    /**
     * SOLIDWORKS 中垂直与锁定配合没有可切换的同向/反向分支：
     * 垂直由 90 度角唯一约束方向，锁定则直接保持完整相对姿态。
     */
    return false;
  }

  if (mateType !== AssemblyMateType.Distance) {
    return true;
  }

  const pair = resolveMateCandidatePair(candidates);
  if (!pair) {
    return false;
  }

  return resolveStandardMateDistanceAlignmentEnabled(pair[0].entityRef, pair[1].entityRef);
}

/**
 * 同轴心锁定旋转可用性上下文。
 */
export interface AssemblyMateLockRotationEnabledContext {
  /** 当前文档内已经提交的 Mate Definition。 */
  readonly mateDefinitions?: readonly AssemblyMateDefinition[];
}

/**
 * 解析同轴心锁定旋转是否可用。
 *
 * @param mateType 当前配合类型。
 * @param candidates 当前选择候选。
 * @param context 当前 Mate 文档上下文。
 * @returns 是否允许锁定旋转。
 */
export function resolveAssemblyMateLockRotationEnabled(
  mateType: AssemblyMateType | null,
  candidates: readonly AssemblyMateSelectionCandidate[],
  context: AssemblyMateLockRotationEnabledContext = {},
): boolean {
  if (mateType !== AssemblyMateType.Concentric) {
    return false;
  }
  const pair = resolveMateCandidatePair(candidates);
  if (!pair || !canLockConcentricMateRotationEntities(pair[0].entityRef, pair[1].entityRef)) {
    return false;
  }

  return !isConcentricLockRotationBlockedByOverDefinedReference({
    mateDefinitions: context.mateDefinitions ?? [],
    selectedEntities: [
      pair[0].entityRef,
      pair[1].entityRef,
    ],
  });
}

/**
 * 判断配合提交状态。
 *
 * @param status 求解状态。
 * @returns 是否可提交。
 */
export function canSubmitAssemblyMateSolveStatus(status: MateSolveStatus): boolean {
  return status === MateSolveStatus.Solved || status === MateSolveStatus.NotRun;
}

/**
 * PropertyManager 诊断展示选项。
 */
export interface AssemblyMateDisplayDiagnosticOptions {
  /**
   * 当前求解器聚合后的总体状态。
   *
   * 说明：
   * 预览草稿和已提交 Mate 一起试算时，诊断数组可能同时存在历史 Mate 的过定义诊断和
   * 当前临时 Mate 的未解诊断。UI 必须优先展示与总体状态一致的诊断，否则会把“当前草稿
   * 不可确认”误报成“装配体已过定义”。
   */
  readonly preferredStatus?: MateSolveStatus;
}

/**
 * Mate PropertyManager 预览状态输入。
 */
export interface AssemblyMatePropertyManagerPreviewStateInput {
  /** 求解器聚合后的原始状态。 */
  readonly solveStatus: MateSolveStatus;
  /** 求解器输出的逐条诊断。 */
  readonly diagnostics: readonly AssemblyMateSolveDiagnostic[];
  /** 当前 PropertyManager 尚未提交的 Mate ID。 */
  readonly transientMateId: string;
}

/**
 * Mate PropertyManager 应展示和提交判断使用的状态。
 */
export interface AssemblyMatePropertyManagerPreviewState {
  /** 只归因到当前草稿 Mate 的状态。 */
  readonly solveStatus: MateSolveStatus;
  /** 只归因到当前草稿 Mate 的诊断；没有当前错误时为 null。 */
  readonly diagnostic: AssemblyMateSolveDiagnostic | null;
}

/**
 * 判断诊断是否会阻止当前草稿 Mate 提交。
 *
 * @param diagnostic 求解器诊断。
 * @returns 是否为阻塞诊断。
 */
function isBlockingAssemblyMateDiagnostic(diagnostic: AssemblyMateSolveDiagnostic): boolean {
  return diagnostic.status !== MateSolveStatus.Solved
    && diagnostic.status !== MateSolveStatus.NotRun;
}

/**
 * 解析当前草稿 Mate 的阻塞状态。
 *
 * @param diagnostics 当前草稿 Mate 的阻塞诊断集合。
 * @returns 应展示给 PropertyManager 的状态。
 */
function resolveTransientMateBlockingStatus(
  diagnostics: readonly AssemblyMateSolveDiagnostic[],
): MateSolveStatus {
  if (diagnostics.some((item) => item.status === MateSolveStatus.MissingReference)) {
    return MateSolveStatus.MissingReference;
  }
  if (diagnostics.some((item) => item.status === MateSolveStatus.UnsupportedGeometry)) {
    return MateSolveStatus.UnsupportedGeometry;
  }

  /**
   * 草稿 Mate 即使收到 overDefined 诊断，也不能直接把 PropertyManager 顶部显示为装配体过定义。
   * 在 SOLIDWORKS 语义里，未提交 Mate 只能表示“当前草稿不能确认”，真实 over-defined 留给已提交 Mate 链路。
   */
  return MateSolveStatus.Unsolved;
}

/**
 * 创建当前草稿 Mate 的展示诊断。
 *
 * @param diagnostic 求解器原始诊断。
 * @param solveStatus 归一化后的草稿状态。
 * @returns PropertyManager 可展示诊断。
 */
function createTransientMateDisplayDiagnostic(
  diagnostic: AssemblyMateSolveDiagnostic,
  solveStatus: MateSolveStatus,
): AssemblyMateSolveDiagnostic {
  if (diagnostic.status !== MateSolveStatus.OverDefined) {
    return {
      ...diagnostic,
      status: solveStatus,
    };
  }

  return {
    mateId: diagnostic.mateId,
    status: solveStatus,
    message: '当前临时配合与现有配合冲突，请修改配合类型、实体或尺寸后再确认。',
  };
}

/**
 * 判断诊断文案是否仍然携带文档级过定义语义。
 *
 * @param errorMessage 控制器即将发布给 UI 的错误文案。
 * @returns 文案是否应从新建草稿状态中移除。
 */
function isDocumentOverDefinedErrorMessage(errorMessage: string | null | undefined): boolean {
  if (typeof errorMessage !== 'string' || errorMessage.trim().length === 0) {
    return false;
  }

  /**
   * 求解器、设计树和历史 Mate 仍然可以使用 overDefined 状态；
   * 这里只处理 PropertyManager 新建草稿顶部文案，防止 stale errorMessage 绕过 solveStatus 归一策略。
   */
  return /过定义|over[- ]?defined/i.test(errorMessage);
}

/**
 * 创建新建 Mate 草稿无法确认时的统一诊断文案。
 *
 * @returns 面向 PropertyManager 顶部区域的草稿诊断。
 */
function createCurrentDraftUnsolvedMessage(): string {
  return '当前配合与现有配合冲突，请修改配合类型、实体或尺寸后再确认。';
}

/**
 * 解析 Mate PropertyManager 当前草稿的预览状态。
 *
 * 设计说明：
 * 1. 求解器会保留已提交 Mate 的诊断，便于设计树和文档状态定位历史错误；
 * 2. PropertyManager 当前正在创建或编辑的是一条尚未提交的草稿 Mate，顶部状态必须只反映这条草稿；
 * 3. 如果历史 Mate 已经 overDefined，但当前草稿本身没有阻塞诊断，应允许继续预览和确认，行为与 SOLIDWORKS 一致。
 *
 * @param input 求解器原始预览状态。
 * @returns PropertyManager 应使用的当前草稿状态。
 */
export function resolveAssemblyMatePropertyManagerPreviewState(
  input: AssemblyMatePropertyManagerPreviewStateInput,
): AssemblyMatePropertyManagerPreviewState {
  if (input.solveStatus === MateSolveStatus.NotRun && input.diagnostics.length === 0) {
    return {
      solveStatus: MateSolveStatus.NotRun,
      diagnostic: null,
    };
  }

  const transientDiagnostics = input.diagnostics.filter((diagnostic) => (
    diagnostic.mateId === input.transientMateId
    && isBlockingAssemblyMateDiagnostic(diagnostic)
  ));

  if (transientDiagnostics.length === 0) {
    return {
      solveStatus: MateSolveStatus.Solved,
      diagnostic: null,
    };
  }

  const solveStatus = resolveTransientMateBlockingStatus(transientDiagnostics);
  const preferredDiagnostic = transientDiagnostics.find((diagnostic) => diagnostic.status === solveStatus)
    ?? transientDiagnostics[0]!;
  return {
    solveStatus,
    diagnostic: createTransientMateDisplayDiagnostic(preferredDiagnostic, solveStatus),
  };
}

/**
 * 归一化 Mate PropertyManager 的控制器状态。
 *
 * 设计说明：
 * 1. `overDefined` 是已提交装配体配合链路的文档级诊断，不应在新建草稿阶段裸露给 PropertyManager；
 * 2. 新建草稿若真的与已有配合冲突，应显示为当前草稿不可确认，并由提交按钮阻止写入；
 * 3. 编辑既有 Mate 时保留原始状态，因为用户正在处理的对象已经属于文档配合链路。
 *
 * @param state 即将发布给 UI 的控制器状态。
 * @returns 归一化后的控制器状态。
 */
export function normalizeAssemblyMatePropertyManagerControllerState(
  state: AssemblyMateControllerState,
): AssemblyMateControllerState {
  if (state.editingMateId) {
    return state;
  }

  if (
    state.solveStatus === MateSolveStatus.Unsolved
    && isDocumentOverDefinedErrorMessage(state.errorMessage)
  ) {
    return {
      ...state,
      errorMessage: createCurrentDraftUnsolvedMessage(),
      canSubmit: false,
    };
  }

  if (state.solveStatus !== MateSolveStatus.OverDefined) {
    return state;
  }

  /**
   * PropertyManager 的 UI 命令态和 cad-core 控制器 active 标志可能在打开/关闭、热更新、
   * 面板重新订阅时短暂不同步。只要不是编辑已提交 Mate，就仍然属于新建草稿语义，
   * 不能把文档级 over-defined 裸露给顶部错误提示；真实文档诊断由 Mate Document
   * 和设计树投影负责展示。
   */
  return {
    ...state,
    solveStatus: MateSolveStatus.Unsolved,
    errorMessage: createCurrentDraftUnsolvedMessage(),
    canSubmit: false,
  };
}

/**
 * 解析应展示给 PropertyManager 的首条有效求解诊断。
 *
 * 设计说明：
 * 1. 求解器会为每条 Mate 输出诊断，前面的诊断可能只是 `solved`；
 * 2. UI 若直接读取 `diagnostics[0]`，会丢失后续真正的 overDefined / unsolved 原因；
 * 3. 临时 Mate 预览会把已提交 Mate 与草稿 Mate 放到同一诊断数组里，优先匹配总体状态；
 * 4. 这里统一跳过成功与未运行诊断，让控制器和持久化状态都保留可定位的错误文案。
 *
 * @param diagnostics 求解器输出的诊断集合。
 * @param options 诊断展示选项。
 * @returns 第一条需要展示的诊断；不存在时返回 null。
 */
export function resolveDisplayAssemblyMateSolveDiagnostic(
  diagnostics: readonly AssemblyMateSolveDiagnostic[],
  options: AssemblyMateDisplayDiagnosticOptions = {},
): AssemblyMateSolveDiagnostic | null {
  if (
    options.preferredStatus === MateSolveStatus.Solved
    || options.preferredStatus === MateSolveStatus.NotRun
  ) {
    /**
     * 当前 PropertyManager 草稿若已经可解，历史 Mate 的 warning/error 仍会留在 diagnostics 供文档层使用，
     * 但顶部错误区不应再展示这些历史诊断，否则用户会看到“当前配合过定义”的误报。
     */
    return null;
  }

  const displayDiagnostics = diagnostics.filter((diagnostic) => (
    diagnostic.status !== MateSolveStatus.Solved
    && diagnostic.status !== MateSolveStatus.NotRun
  ));
  if (options.preferredStatus) {
    const preferredDiagnostic = displayDiagnostics.find((diagnostic) => (
      diagnostic.status === options.preferredStatus
    ));
    if (preferredDiagnostic) {
      return preferredDiagnostic;
    }
  }
  return displayDiagnostics[0] ?? null;
}

/**
 * 创建提交 Mate 后的控制器状态补丁。
 *
 * 设计说明：
 * 新建 Mate 时，SOLIDWORKS 会保持 PropertyManager 打开，便于用户继续创建下一条配合；
 * 编辑既有 Mate 时，确认后应退出编辑态。因此该策略只描述状态变化，不触碰文档、预览或场景对象。
 *
 * @param state 当前控制器状态。
 * @returns 提交后的状态补丁。
 */
export function createPostSubmitAssemblyMateControllerStatePatch(
  state: AssemblyMateControllerState,
): Partial<AssemblyMateControllerState> {
  if (state.editingMateId) {
    return createDefaultAssemblyMateControllerState();
  }
  return createEmptyAssemblyMateControllerSessionStatePatch();
}
